import React, { Fragment, useMemo } from 'react';

import { getMeta } from '@helpcenter/meta';
import { type PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';

import { AiWidgetConfigProvider } from '@atlassian/csm-ai-search-trigger';

import type { supportWidgetConfigProviderQuery } from './__generated__/supportWidgetConfigProviderQuery.graphql';
import {
    handleChatBubbleVariant,
    handleWidgetButtonVariant,
    handleWidgetPosition,
    handleWidgetRadiusType,
    handleWidgetSpaceType,
} from './utils';

/**
 * Connects the react-resource-router
 */
export const SupportWidgetConfigPreloadedQueryProvider = ({
    queryReference,
    children,
}: {
    queryReference: PreloadedQuery<supportWidgetConfigProviderQuery>;
    children: React.ReactNode;
}) => {
    const data = usePreloadedQuery(
        graphql`
            query supportWidgetConfigProviderQuery($helpCenterAri: ID!, $cloudId: ID!) {
                helpCenter(cloudId: $cloudId) {
                    helpCenterById(helpCenterAri: $helpCenterAri) @optIn(to: "HelpCenterAggBeta") {
                        ... on HelpCenter {
                            helpCenterBranding @optIn(to: ["HelpCenterBrandingTest"]) {
                                colors {
                                    primary
                                }
                            }
                        }
                    }
                }
                csmAi_getSupportWidget(helpCenterAri: $helpCenterAri) @optIn(to: "CsmAiHub") {
                    ... on CsmAiWidgetConfig {
                        id
                        isEnabled
                        position
                        theme {
                            colorVibeVariant
                            radius
                            space
                            chatColor
                        }
                    }
                }
            }
        `,
        queryReference
    );
    const primaryColor = data.helpCenter?.helpCenterById?.helpCenterBranding?.colors?.primary;
    const widgetConfig = data.csmAi_getSupportWidget;
    const siteId = getMeta('tenant-id');

    const supportWidgetConfig = useMemo(():
        | React.ComponentProps<typeof AiWidgetConfigProvider>['supportWidgetConfig']
        | null => {
        if (!widgetConfig || !widgetConfig.id || !widgetConfig.theme || !primaryColor || !siteId) {
            return null;
        }

        return {
            siteId,
            widgetId: widgetConfig.id,
            widgetTheme: {
                primaryColor,
                radius: handleWidgetRadiusType(widgetConfig.theme.radius),
                space: handleWidgetSpaceType(widgetConfig.theme.space),
                chatBubbleVariant: handleChatBubbleVariant(widgetConfig.theme.chatColor),
                widgetButtonVariant: handleWidgetButtonVariant(widgetConfig.theme.colorVibeVariant),
                attribution: null,
            },
            widgetPosition: handleWidgetPosition(widgetConfig.position),
            isEnabled: Boolean(widgetConfig.isEnabled),
        };
    }, [widgetConfig, primaryColor, siteId]);

    if (!supportWidgetConfig) {
        return <Fragment>{children}</Fragment>;
    }

    return <AiWidgetConfigProvider supportWidgetConfig={supportWidgetConfig}>{children}</AiWidgetConfigProvider>;
};
